/**
 * SpaceLogo — a Space's logo everywhere a Space is shown.
 *
 * Renders the cleartext or decrypted logo, falling back to initials (or a
 * lock while an encrypted Space's Cipher is missing). Encrypted logos are
 * only fetched once the element is on screen, so long lists (directory,
 * sidebar folders) do not download every logo up front.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useSpaceIdentityMedia,
  type SpaceIdentityMediaSpace,
} from '../pages/spaces/useSpaceIdentityMedia';
import { Icon } from '../icons/Icon';
import '../styles/_space-logo.scss';
import { Tooltip } from './Tooltip';

export type SpaceLogoSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export const SPACE_LOGO_SIZES: Record<SpaceLogoSize, number> = {
  xs: 20,
  sm: 24,
  md: 32,
  lg: 64,
  xl: 96,
};

export interface SpaceLogoProps {
  space: SpaceIdentityMediaSpace;
  /** Resolved (decrypted) display name, used for initials and alt text. */
  name: string;
  size?: SpaceLogoSize;
  className?: string;
}

function graphemes(text: string): string[] {
  const Segmenter = (Intl as unknown as { Segmenter?: typeof Intl.Segmenter }).Segmenter;
  if (Segmenter) {
    return Array.from(new Segmenter(undefined, { granularity: 'grapheme' }).segment(text), (s) => s.segment);
  }
  return Array.from(text);
}

/** Up to two initials from a Space name; emoji and non-Latin scripts are kept whole. */
export function spaceInitials(name: string): string {
  const [firstWord, secondWord] = name.trim().split(/\s+/).filter(Boolean);
  if (!firstWord) return '?';
  const first = graphemes(firstWord)[0] ?? '';
  const second = secondWord ? (graphemes(secondWord)[0] ?? '') : '';
  return `${first}${second}`.toLocaleUpperCase();
}

/** On-screen signal. Without IntersectionObserver (tests, old browsers) treat as visible. */
function useOnScreen<T extends Element>(): [React.RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const supported = typeof IntersectionObserver !== 'undefined';
  const [visible, setVisible] = useState(!supported);

  useEffect(() => {
    if (!supported || !ref.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) setVisible(entry.isIntersecting);
      },
      { rootMargin: '200px' },
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [supported]);

  return [ref, visible];
}

interface SpaceLogoFrameProps {
  name: string;
  size: SpaceLogoSize;
  className: string;
  url: string | null;
  state: string;
  frameRef?: React.RefObject<HTMLSpanElement>;
}

function SpaceLogoFrame({ name, size, className, url, state, frameRef }: SpaceLogoFrameProps) {
  const { t } = useTranslation();
  const px = SPACE_LOGO_SIZES[size];
  return (
    <span
      ref={frameRef}
      className={`space-logo space-logo--${size} ${className}`.trim()}
      style={{ width: px, height: px }}
      role="img"
      aria-label={t('spaces.logo.alt', { name })}
      data-state={state}
    >
      {url ? (
        <img src={url} alt="" className="space-logo-image" draggable={false} />
      ) : state === 'locked' ? (
        <Tooltip content={t('spaces.logo.locked')}>
          <span className="space-logo-locked" role="img" aria-label={t('spaces.logo.locked')}>
            <Icon name="lock" size="xs" />
          </span>
        </Tooltip>
      ) : (
        <span className="space-logo-initials" aria-hidden="true">
          {spaceInitials(name)}
        </span>
      )}
    </span>
  );
}

/** Encrypted logo: decrypt the pointer and fetch the blob once on screen. */
function EncryptedSpaceLogo({ space, name, size = 'md', className = '' }: SpaceLogoProps) {
  const [ref, visible] = useOnScreen<HTMLSpanElement>();
  const { url, state } = useSpaceIdentityMedia(space, 'logo', { visible });
  return <SpaceLogoFrame name={name} size={size} className={className} url={url} state={state} frameRef={ref} />;
}

export function SpaceLogo(props: SpaceLogoProps) {
  const { space, name, size = 'md', className = '' } = props;
  // Cleartext logos need no cipher or E2E machinery, which keeps long lists cheap.
  if (space.encryptIdentity && space.encryptedLogo) return <EncryptedSpaceLogo {...props} />;
  const url = space.encryptIdentity ? null : (space.logoUrl ?? null);
  return (
    <SpaceLogoFrame
      name={name}
      size={size}
      className={className}
      url={url}
      state={url ? 'available' : 'none'}
    />
  );
}
